import { format } from "date-fns";

import { YesNo } from "@covertree/types";
import { PreciselyAddressWithUnit } from "@covertree/features";
import { Toast } from "@covertree/utils";

import {
  AddressInput,
  CreateClaimInput,
  EmergencyServiceContactInput,
  EmergencyServiceContactType,
  FileUploadUrl,
  LossType,
} from "@api/operations.generated";
import { RootState } from "@redux/store";
import { ClaimDetailsForm } from "@redux/slices";

import { WaterDamageSourceForm } from "../../EnterClaimDetails/WaterDamage/form";
import { InjuredPerson } from "../../EnterClaimDetails/shared/InjuredPersonsSection/form";
import { FireOrSmokeDamageForm } from "../../EnterClaimDetails/FireOrSmokeDamage/form";
import { WeatherOrPropertyDamageForm } from "../../EnterClaimDetails/WeatherOrPropertyDamage/form";
import { TheftOrVandalismForm } from "../../EnterClaimDetails/TheftOrVandalism/form";
import { LiabilityForm } from "../../EnterClaimDetails/Liability/form";

export const mapPreciselyAddressToAddressInput = ({
  city,
  country,
  lotUnit,
  state,
  postCode,
  mainAddressLine,
}: PreciselyAddressWithUnit): AddressInput => {
  return {
    city,
    countryCode: country,
    lotUnit: lotUnit || undefined,
    stateCode: state,
    streetAddress: mainAddressLine,
    zipCode: postCode,
  };
};

export const buildInjuredPeople = (
  claimDetailsValue: Exclude<ClaimDetailsForm, WaterDamageSourceForm>
) => {
  return claimDetailsValue.injuredPeople.map(
    (injuredPerson: InjuredPerson) => ({
      firstName: injuredPerson.firstName,
      lastName: injuredPerson.lastName,
      address: mapPreciselyAddressToAddressInput(injuredPerson.address.value),
      phoneNumber: injuredPerson.phoneNumber,
    })
  );
};

export const buildEmergencyServiceContact = (
  claimDetailsValue: FireOrSmokeDamageForm
) => {
  const result: EmergencyServiceContactInput[] = [];

  if (claimDetailsValue.fireDepartment) {
    result.push({
      contactType: EmergencyServiceContactType.FireDepartment,
      departmentName: claimDetailsValue.fireDepartment,
    });
  }

  if (claimDetailsValue.policeDepartment) {
    result.push({
      contactType: EmergencyServiceContactType.PoliceDepartment,
      departmentName: claimDetailsValue.policeDepartment,
    });
  }

  return result;
};

export const buildFireOrSmokeDamage = (
  claimDetailsValue: FireOrSmokeDamageForm
) => {
  const result: Pick<
    CreateClaimInput,
    "emergencyServiceContacts" | "fireDepartmentContacted"
  > = {};

  if (claimDetailsValue.fireDepartment || claimDetailsValue.policeDepartment) {
    result.emergencyServiceContacts =
      buildEmergencyServiceContact(claimDetailsValue);
  }

  if (claimDetailsValue.fireResponse) {
    result.fireDepartmentContacted =
      claimDetailsValue.fireResponse === YesNo.Yes;
  }

  return result;
};

export const buildWaterDamageSource = (
  claimDetailsValue: WaterDamageSourceForm
) => {
  return {
    damageRepaired: claimDetailsValue.isDamageFixed,
    sourceOfDamageWater: claimDetailsValue.claimType,
  };
};

export const buildWeatherOrPropertyDamage = (
  claimDetailsValue: WeatherOrPropertyDamageForm
) => {
  return {
    damageRepaired: claimDetailsValue.isDamageFixed,
    sourceOfDamageWeather: claimDetailsValue.claimType,
  };
};

export const buildTheftOrVandalism = (
  claimDetailsValue: TheftOrVandalismForm
) => {
  return {
    theftOrVandalismClaimType: claimDetailsValue.claimType,
  };
};

export const buildLiability = (claimDetailsValue: LiabilityForm) => {
  return {
    liabilityClaimReason: claimDetailsValue.claimReason,
  };
};

export const mapAppStateToCreateClaimInput = (
  state: RootState
): CreateClaimInput => {
  const { policyDetails, lossDetails, claimDetails, claimReporter } = state;

  const dateOfLoss = policyDetails.dateOfLoss;
  const reportingParty = claimReporter;
  const lossType = lossDetails.lossType!;
  const lossAddress = lossDetails.lossAddress;
  const damageDescription = claimDetails.value.claimDescription;
  const policyLocator = policyDetails.policyLocator;

  const result: CreateClaimInput = {
    dateOfLoss: format(dateOfLoss, "yyyy-MM-dd"),
    reportingParty,
    policyLocator,
    lossType,
    lossAddress,
    damageDescription,
  };

  const claimDetailsValue = claimDetails.value;

  // Add propertyAddress when policy is found by address query
  if (policyDetails.address) {
    result.propertyAddress = mapPreciselyAddressToAddressInput(
      policyDetails.address.value
    );
  }

  // Map InjuredPeople to Injured Parties property
  if (
    "wasAnyoneInjured" in claimDetailsValue &&
    claimDetailsValue?.wasAnyoneInjured === YesNo.Yes
  ) {
    result.injuredParties = buildInjuredPeople(claimDetailsValue);
  }

  // Add FireOrSmokeDamage options when it's a FireOrSmokeDamage claim
  if (claimDetails.type === LossType.FireOrSmokeDamage) {
    Object.assign(
      result,
      buildFireOrSmokeDamage(claimDetailsValue as FireOrSmokeDamageForm)
    );
  }

  // Map WaterDamage options when it's a WaterDamage claim
  if (claimDetails.type === LossType.WaterDamage) {
    Object.assign(
      result,
      buildWaterDamageSource(claimDetailsValue as WaterDamageSourceForm)
    );
  }

  // Map WeatherOrPropertyDamage options when it's a WeatherOrPropertyDamage claim
  if (claimDetails.type === LossType.WeatherOrPropertyDamage) {
    Object.assign(
      result,
      buildWeatherOrPropertyDamage(
        claimDetailsValue as WeatherOrPropertyDamageForm
      )
    );
  }

  // Map TheftOrVandalism options when it's a TheftOrVandalism claim
  if (claimDetails.type === LossType.TheftOrVandalism) {
    Object.assign(
      result,
      buildTheftOrVandalism(claimDetailsValue as TheftOrVandalismForm)
    );
  }

  // Map Liability options when it's a Liability claim
  if (claimDetails.type === LossType.Liability) {
    Object.assign(result, buildLiability(claimDetailsValue as LiabilityForm));
  }

  return result;
};

export const uploadFiles = async (fileUrls: FileUploadUrl[], files: File[]) => {
  try {
    for (const fileUrl of fileUrls) {
      const file = files.find((file) => file.name === fileUrl.fileName);
      if (!file) {
        return;
      }
      const arrayBuffer = await file.arrayBuffer();
      await fetch(fileUrl.uploadUrl, {
        method: "PUT",
        body: arrayBuffer,
      });
    }
  } catch (e) {
    Toast.showError("An error occurred while uploading files.");
    console.error("Error uploading files: ", e);
  }
};
